import React, { useEffect, useState } from 'react';
import { Building } from '../../../confi-state';
import { Segment } from '../models/segment';
import { ColumnService } from './column-service';
import { columnDefs, columnSubtype, subtypeEnum, variantEnum } from './models/column-definition';
import { columnType } from './models/column-type.enum';
import { headType } from './models/head-type.enum';
import { HybridColumn, PropsHybrid, HybridPart } from './models/props-config';
import { segmentType } from './models/segment-type';
import { partTypeEnum } from './models/part-type.enum';
import Refine from '../Refine/Refine';
import { Part } from '../models/part';
import { RefineService } from '../Refine/refine.service';
import { ServiceManager } from '../../../services/service-manager';
import { buildingType } from '../models/building-type.enum';
import HybridPartSelector from './HybridPartSelector/HybridPartSelector';
import { SectionJSON } from '../../../models/section-json';
import { HybridService } from './hybrid.service';
import HybridSegmentSelector from './HybridSegmentSelector/HybridSegmentSelector';

const buildingChange = (buildingProps: PropsHybrid, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, true);
};

const HybridRefined = ({ currentBuilding, onChangeBuilding, sectionJson }: { currentBuilding: Building, onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void, sectionJson: SectionJSON }) => {

    const [buildingProps, setBuildingProps] = useState<PropsHybrid>(
        undefined
    );
    const [activePart, setActivePart] = useState<partTypeEnum>(
        partTypeEnum.body
    );

    const getColumnTypes = (partType: partTypeEnum) => {
        let cols;
        if (partType === partTypeEnum.headLeft || partType === partTypeEnum.headRight) {
            cols = columnDefs.filter(s => s.columnSubtypes.includes(columnSubtype.head));
        } else if (partType === partTypeEnum.body) {
            cols = columnDefs.filter(s => (s.columnSubtypes.includes(columnSubtype.body) || s.columnSubtypes.includes(columnSubtype.moosplaza)));
        }
        return cols.map(c => c.type);
    }


    const setUpDefaultColumnFromDefinition = (ct: headType | columnType, p: partTypeEnum) => {
        const coldef = columnDefs.find(c => c.type === ct)
        const columnService = new ColumnService();
        const segments = RefineService.getSegmentTypes(ct, columnDefs);
        if (segments && segments.length > 0) {
            let seg: Segment<segmentType, subtypeEnum, variantEnum>[] = []
            let amountOfUnits = [];
            segments.forEach(s => {
                const sg = RefineService.getSegmentDefinitionsForType(ct, s, columnDefs);
                const segDef = sg[0];
                let st = segDef.subtype;

                const floorDef = segDef.floorVariants[0];
                const floors = floorDef.floors;

                let variantDef = floorDef.variants[0];
                if (p === partTypeEnum.headRight && floorDef.variants.length === 2) {
                    variantDef = floorDef.variants[1];
                }

                seg.push({
                    type: s as segmentType,
                    subtype: st,
                    sectionTypes: variantDef.sections,
                    floorsInSegment: floors
                })
            })
            const col: HybridColumn = {
                id: Math.random().toString(),
                type: ct,
                bays: coldef.bays,
                segments: seg,
                amountOfUnits
            }

            return col;
        }
    }

    const getDefaultColumnTypeForPart = (part: HybridPart) => {
        let type;
        if (part.type === partTypeEnum.body) {
            type = columnType.G_M;
        } else if (part.type === partTypeEnum.headLeft) {
            type = columnType.O_TLH2;
        } else if (part.type === partTypeEnum.headRight) {
            type = columnType.O_TLH2;
        }
        return type;
    }

    useEffect(() => {
        if (currentBuilding && currentBuilding.props) {
            const buildingProps = currentBuilding.props as PropsHybrid;
            if (buildingProps && (!buildingProps.partsRefined || buildingProps.partsRefined.length === 0)) {
                const bp: any = { ...buildingProps, parts: [...buildingProps.parts] }
                // currently this is a single entity, but it might be expanded in the future
                bp.partsRefined = [{
                    id: 'first',
                    parts: ServiceManager.buildingHelperService.cloneParts(bp.parts)
                }];
                setBuildingProps({ ...bp });
                buildingChange({ ...bp }, currentBuilding, onChangeBuilding);
            } else {
                setBuildingProps(buildingProps);
            }
        }
        //eslint-disable-next-line
    }, [currentBuilding]);

    const getPartName = (p: partTypeEnum) => {
        let name = '';
        switch (p) {
            case partTypeEnum.headLeft:
                name = 'Kop 1'
                break;
            case partTypeEnum.body:
                name = 'Body'
                break;
            case partTypeEnum.headRight:
                name = 'Kop 2'
                break;
        }
        return name;
    }

    const getSegmentName = (p: segmentType) => {
        let name = '';
        switch (p) {
            case segmentType.plinth:
                name = 'Plint';
                break;
            case segmentType.middle:
                name = 'Midden';
                break;
            case segmentType.crown:
                name = 'Kroon'
                break;
        }
        return name;
    }


    const getSegmentSubtypeName = (p: subtypeEnum) => {
        let name = '';
        switch (p) {
            case subtypeEnum.H:
                name = 'Maisonette';
                break;
            case subtypeEnum.G:
                name = 'Appartement';
                break;
            case subtypeEnum.single:
                name = 'Geen'
                break;
        }
        return name;
    }

    const getVariantName = (variant: variantEnum): string => {
        if (variant === variantEnum.left) {
            return 'Links';
        } else if (variant === variantEnum.right) {
            return 'Rechts';
        } else {
            return 'Midden';
        }
    }

    const updateBuildingColumn = (part, column) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partTypeToAdd = part.type;
        const partToAdd = bp.partsRefined[0].parts.find(p => p.type === partTypeToAdd);
        let columns = partToAdd.columns;
        columns = columns.map(col => col.id === column.id ? column : col);
        partToAdd.columns = columns;

        bp.partsRefined[0].parts = ServiceManager.babylonService.hybridBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            bp.moosPlazaAlignment,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts as any);

        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts);

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    const updateBuildingColumns = (part, columns) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partToAdd = part.type;
        const partAdd = bp.partsRefined[0].parts.find(p => p.type === partToAdd);
        partAdd.columns = columns;

        bp.partsRefined[0].parts = ServiceManager.babylonService.hybridBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            bp.moosPlazaAlignment,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts as any);

        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = [...columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts)];

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    return (
        <div className="group" data-testid="HybridRefined">
            {buildingProps && buildingProps.partsRefined &&
                <>
                    <div>
                        <h2 className="greyed-out-text">Gebouw verfijnen</h2>
                        <HybridPartSelector activePart={activePart} setActivePart={setActivePart} getPartName={getPartName} />
                        <Refine
                            part={buildingProps.partsRefined[0].parts.find(p => p.type === activePart)}
                            bt={currentBuilding.type}
                            buildingId={currentBuilding.id as string}
                            sectionJson={sectionJson}
                            segmentTypesMultipleFloors={[segmentType.middle]}
                            segmentTypesAllowCustom={[segmentType.plinth]}
                            columnDefs={columnDefs}
                            SegmentComponent={HybridSegmentSelector}
                            floorHeight={ServiceManager.buildingHelperService.floorHeightOfBuilding(buildingType.hybrid)}
                            updateBuildingColumn={updateBuildingColumn}
                            updateBuildingColumns={updateBuildingColumns}
                            getDefaultColumnTypeForPart={getDefaultColumnTypeForPart}
                            setUpDefaultColumnFromDefinition={setUpDefaultColumnFromDefinition}
                            getVariantName={getVariantName as any}
                            getSegmentName={getSegmentName as any}
                            getSegmentSubtypeName={getSegmentSubtypeName as any}
                        />

                    </div>
                </>
            }
        </div>
    )
};

export default HybridRefined;
